<!-- MenuRecursive.vue -->
<template>
  <div class="menu-recursive">
    <div v-for="(menu, index) in menus" :key="menu.index || index">
      <!-- 有子菜单的项（支持点击跳转 + 展开折叠） -->
      <el-submenu
        v-if="menu.children && menu.children.length"
        :index="menu.index"
        :tabindex="menu.disabled ? -1 : 0"
        :aria-label="menu.label"
        :class="getMenuClass(level)"
        @keydown.native="handleMenuKey($event, menu)"
      >
        <template slot="title">
          <!-- 1. 文字区域：点击跳转 -->
          <!--stop阻止冒泡，避免触发展开 -->
          <i v-if="menu.icon" :class="`el-icon-${menu.icon}`"></i>
          <PersonalMenuLabel
            :label="menu.label"
            class="menu-text"
            :class="{ 'menu-disabled': menu.disabled }"
            @click.native.stop="handleJump(menu)"
          />

          <!-- 2. 箭头区域：保留默认展开/折叠（使用el-submenu自带的箭头） -->
          <!-- 无需额外代码，el-submenu默认箭头会处理展开逻辑 -->
        </template>

        <!-- 递归渲染子菜单 -->
        <menu-recursive
          :menus="menu.children"
          :level="level + 1"
          :unreadCount="unreadCount"
        ></menu-recursive>
      </el-submenu>

      <!-- 无子菜单的项：直接跳转 -->
      <el-menu-item
        v-else
        :index="menu.index"
        :disabled="menu.disabled"
        :tabindex="menu.disabled ? -1 : 0"
        :aria-label="menu.label"
        @keydown.native="handleMenuKey($event, menu)"
        :class="[
          getMenuClass(level),
          {
            'menu-disabled': menu.disabled,
            'menu-active': isActiveRoute(menu.index),
          },
        ]"
      >
        <i v-if="menu.icon" :class="`el-icon-${menu.icon}`"></i>
        <PersonalMenuLabel class="menu-text" :label="menu.label" />
        <span
          v-if="menu.index === '/pc/myxg/messageCenter' && unreadCount > 0"
          class="unread-badge"
        >
          {{ unreadCount > 99 ? "99+" : unreadCount }}
        </span>
      </el-menu-item>
    </div>
  </div>
</template>

<script>
import PersonalMenuLabel from "./PersonalMenuLabel.vue";

export default {
  name: "MenuRecursive",
  components: { PersonalMenuLabel },
  props: {
    menus: {
      type: Array,
      default: () => [],
    },
    level: {
      type: Number,
      default: 1,
    },
    unreadCount: {
      type: Number,
      default: 0,
    },
  },
  methods: {
    handleMenuKey(event, menu) {
      // 只处理当前行，避免子菜单的键盘操作同时触发父级跳转。
      if (event.target !== event.currentTarget || !["Enter", " "].includes(event.key)) return;
      event.preventDefault();
      if (menu.disabled) return;
      if (event.key === " " && menu.children?.length) {
        event.currentTarget.querySelector(".el-submenu__title")?.click();
      } else {
        this.handleJump(menu);
      }
    },
    // 处理点击文字区域的跳转逻辑
    handleJump(menu) {
      // 只要menu有index且未禁用，就执行跳转（无论层级）
      if (menu.index && !menu.disabled) {
        this.$router.push(menu.index);
      }
    },
    getMenuClass(depth) {
      // 根据菜单在数据结构中的深度来确定样式类
      // 第一层(深度1): menu-depth-1, 第二层(深度2): menu-depth-2, 第三层(深度3): menu-depth-3
      return `menu-depth-${depth}`;
    },
    isActiveRoute(index) {
      return this.$route.path === index;
    },
  },
};
</script>

<style scoped lang="less">
@brand-color: #4286f5;
@text-regular: #606266;
@text-secondary: #909399;
@bg-active: rgba(66, 134, 245, 0.12);
@bg-hover: rgba(66, 134, 245, 0.06);

.menu-recursive {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}
::v-deep .el-menu {
  border-right: none;
  background: transparent;
}

::v-deep .el-menu-item,
::v-deep .el-submenu__title {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 42px;
  line-height: 42px;
  padding: 0 20px;
  font-size: 14px;
  color: @text-regular;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  border-radius: 8px;
  margin: 2px 8px;
}

::v-deep .el-menu-item:hover,
::v-deep .el-submenu__title:hover {
  background-color: @bg-hover;
  color: @brand-color;
}

::v-deep .el-menu-item.is-active,
::v-deep .el-menu-item.menu-active {
  background-color: @bg-active !important;
  color: @brand-color !important;
  border-left: 3px solid @brand-color;
  padding-left: 17px !important;
}

::v-deep .el-menu-item i,
::v-deep .el-submenu__title i {
  flex: 0 0 auto;
  font-size: 16px;
  margin-right: 10px;
  color: @text-secondary;
  transition: color 0.2s ease;
}

::v-deep .el-menu-item:hover i,
::v-deep .el-submenu__title:hover i,
::v-deep .el-menu-item.is-active i,
::v-deep .el-menu-item.menu-active i {
  color: @brand-color;
}

.menu-depth-2 {
  ::v-deep .el-menu-item,
  ::v-deep .el-submenu__title {
    padding-left: 36px;
    font-size: 13px;
  }
}

.menu-depth-3 {
  ::v-deep .el-menu-item,
  ::v-deep .el-submenu__title {
    padding-left: 52px;
    font-size: 13px;
  }
}

/* 文字区域样式：占满除箭头外的空间，方便点击 */
.menu-text {
  cursor: pointer;
}

/* 禁用状态样式 */
.menu-disabled {
  cursor: not-allowed;
  opacity: 0.5;
  pointer-events: none;
}

/* 去除el-submenu标题的默认点击样式（可选） */
::v-deep .el-submenu__title {
  /* 确保文字区域点击时不触发整个标题的高亮（如果需要） */
  user-select: none;
  padding-right: 36px !important;
}

.unread-badge {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 9px;
  background: #ff4d4f;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
}

::v-deep .el-submenu {
  background: transparent !important;
}

::v-deep .el-submenu .el-menu-item {
  margin-left: 0 !important;
  min-width: 0;
}

::v-deep .el-submenu__title .el-submenu__icon-arrow {
  right: 16px;
  margin-right: 0;
}

::v-deep .el-submenu__arrow {
  color: @text-secondary;
}

::v-deep .el-submenu__title:hover .el-submenu__arrow,
::v-deep .el-submenu.is-active .el-submenu__arrow {
  color: @brand-color;
}

@media (prefers-reduced-motion: reduce) {
  ::v-deep .el-menu-item,
  ::v-deep .el-submenu__title,
  ::v-deep .el-menu-item i,
  ::v-deep .el-submenu__title i {
    transition: none;
  }
}
</style>
